import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiGet } from '../../../../api/queries/apiGet';
import { API_BASE_URL } from '../constants/consts';
import { ActiveUsersAndResourcesMode, QueryFiltersDomainOnly } from '../types';
import { EntityDetails, EntityDetailsFull } from '../../types';
import { buildUniversalMfaDomainQueryParams } from '../constants/utils';
import { universalMFAQueryKeys } from '../constants/queryKeys';

const END_POINT = '/metric/activeUsers';
const PREVIEW_MODE_LIMIT = 9;
const FULL_MODE_LIMIT = 50;

type ActiveUsersDto = EntityDetails;
type ActiveUsersFullDto = Omit<EntityDetailsFull, 'lastAuthTime'> & { lastAuthTime: string };

const convertToActiveUsers = (data: ActiveUsersDto[]): EntityDetails[] => {
    return data.map((item) => ({
        name: item.name,
        hits: item.hits,
        guid: item.guid,
        isProtected: item.isProtected
    }));
};

const convertToActiveUsersFull = (data: ActiveUsersFullDto[]): EntityDetailsFull[] => {
    return data.map((item) => ({
        name: item.name,
        hits: item.hits,
        guid: item.guid,
        lastAuthTime: new Date(item.lastAuthTime),
        riskLevel: item.riskLevel,
        domain: item.domain,
        isProtected: item.isProtected
    }));
};

const fetchActiveUsers = async (
    filters: QueryFiltersDomainOnly,
    mode: ActiveUsersAndResourcesMode
): Promise<ActiveUsersDto[] | ActiveUsersFullDto[]> => {
    const data = await apiGet<ActiveUsersDto[] | ActiveUsersFullDto[]>(`${API_BASE_URL}${END_POINT}`, {
        ...buildUniversalMfaDomainQueryParams(filters),
        limit: mode === 'preview' ? PREVIEW_MODE_LIMIT.toString() : FULL_MODE_LIMIT.toString(),
        mode
    });
    return data;
};

export const useActiveUsersPreview = (filters: QueryFiltersDomainOnly): UseQueryResult<EntityDetails[]> => {
    return useQuery({
        queryKey: universalMFAQueryKeys.activeUsers(filters, 'preview'),
        queryFn: () => fetchActiveUsers(filters, 'preview') as Promise<ActiveUsersDto[]>,
        select: convertToActiveUsers
    });
};

export const useActiveUsersFull = (
    filters: QueryFiltersDomainOnly,
    enabled: boolean
): UseQueryResult<EntityDetailsFull[]> => {
    return useQuery({
        queryKey: universalMFAQueryKeys.activeUsers(filters, 'full'),
        queryFn: () => fetchActiveUsers(filters, 'full') as Promise<ActiveUsersFullDto[]>,
        select: convertToActiveUsersFull,
        enabled
    });
};
